import { useState, useEffect, useCallback, useRef } from "react";
import { BubbleMenuState } from "../types";

interface SelectionManagerReturn {
  bubbleMenu: BubbleMenuState;
  selectedText: string;
  setBubbleMenu: (state: BubbleMenuState) => void;
  setSelectedText: (text: string) => void;
  hideBubbleMenu: () => void;
}

/**
 * Hook to manage text selection and bubble menu positioning
 */
export const useSelectionManager = (
  containerRef: React.RefObject<HTMLDivElement>,
  onContentChange?: (content: string) => void
): SelectionManagerReturn => {
  const [bubbleMenu, setBubbleMenu] = useState<BubbleMenuState>({
    isVisible: false,
    position: { x: 0, y: 0 }
  });
  const [selectedText, setSelectedText] = useState("");

  // Use refs to track current state without causing re-renders
  const isVisibleRef = useRef(false);
  const selectedTextRef = useRef("");
  
  // Track if user is actively selecting (dragging)
  const isSelectingRef = useRef(false);
  
  // Simple undo stack for manual deletions (when execCommand doesn't work)
  const undoStackRef = useRef<{ content: string; container: HTMLDivElement }[]>([]);

  const hideBubbleMenu = useCallback(() => {
    // Only update state if it's not already hidden (prevents unnecessary re-renders)
    if (isVisibleRef.current) {
      isVisibleRef.current = false;
      setBubbleMenu({ isVisible: false, position: { x: 0, y: 0 } });
    }
    if (selectedTextRef.current !== "") {
      selectedTextRef.current = "";
      setSelectedText("");
    }
  }, []);

  const handleSelectionChange = useCallback(() => {
    try {
      const selection = window.getSelection();

      // Hide bubble menu if no selection or selection is not in our container
      if (!selection ||
        selection.isCollapsed ||
        !containerRef.current ||
        !containerRef.current.contains(selection.anchorNode)) {
        hideBubbleMenu();
        return;
      }

      const text = selection.toString();

      // Update selected text (always track this)
      if (selectedTextRef.current !== text) {
        selectedTextRef.current = text;
        setSelectedText(text);
      }

      // Don't update bubble menu position while actively selecting (dragging)
      // Wait until selection is complete (mouseup)
      if (isSelectingRef.current) {
        return;
      }

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Update bubble menu position
      isVisibleRef.current = true;
      setBubbleMenu({
        isVisible: true,
        position: {
          x: rect.left + rect.width / 2 - 60, // Center the menu
          y: rect.top - 50 // Position above selection
        }
      });
    } catch (error) {
      console.warn('Selection change error:', error);
      hideBubbleMenu();
    }
  }, [containerRef, hideBubbleMenu]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [handleSelectionChange]);

  // Track mousedown/mouseup to detect active selection
  useEffect(() => {
    const handleMouseDown = () => {
      isSelectingRef.current = true;
    };

    const handleMouseUp = () => {
      isSelectingRef.current = false;
      
      // Update bubble menu position now that selection is complete
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && 
            !selection.isCollapsed && 
            containerRef.current?.contains(selection.anchorNode)) {
          try {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            
            isVisibleRef.current = true;
            setBubbleMenu({
              isVisible: true,
              position: {
                x: rect.left + rect.width / 2 - 60, // Center the menu
                y: rect.top - 50 // Position above selection
              }
            });
          } catch (error) {
            console.warn('Error positioning bubble menu:', error);
          }
        }
      }, 10);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [containerRef]);

  // Keydown handler to handle deletion when focus is lost after drag selection
  // This handles the case where drag-selection leaves focus on body
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const selection = window.getSelection();
      
      // Handle Ctrl+Z for undo of manual deletions
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        const lastUndo = undoStackRef.current[undoStackRef.current.length - 1];
        if (lastUndo && containerRef.current === lastUndo.container) {
          e.preventDefault();
          e.stopPropagation();
          
          // Restore the content
          undoStackRef.current.pop();
          containerRef.current.innerHTML = lastUndo.content;
          
          // Notify of content change
          if (onContentChange) {
            onContentChange(lastUndo.content);
          }
          
          // Focus the editor
          containerRef.current.focus({ preventScroll: true });
          return;
        }
      }
      
      // Only handle Backspace and Delete keys
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      
      // Only intercept if there's a selection in our container but focus is elsewhere
      if (
        selection &&
        !selection.isCollapsed &&
        containerRef.current &&
        containerRef.current.contains(selection.anchorNode) &&
        document.activeElement !== containerRef.current
      ) {
        e.preventDefault();
        e.stopPropagation();
        
        // Save content for undo before making changes
        const contentBeforeDelete = containerRef.current.innerHTML;
        
        // Save the selection range before focus
        const range = selection.getRangeAt(0).cloneRange();
        
        // Try to focus using click + focus
        const rect = range.getBoundingClientRect();
        const clickEvent = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX: rect.left,
          clientY: rect.top
        });
        containerRef.current.dispatchEvent(clickEvent);
        containerRef.current.focus({ preventScroll: true });
        
        // Restore the selection after focus
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Check if focus worked
        if (document.activeElement === containerRef.current) {
          // Use execCommand to delete - this integrates with browser's undo stack
          document.execCommand('delete', false);
        } else {
          // Fallback: manual deletion with our own undo stack
          undoStackRef.current.push({
            content: contentBeforeDelete,
            container: containerRef.current
          });
          // Limit undo stack size
          if (undoStackRef.current.length > 50) {
            undoStackRef.current.shift();
          }
          
          range.deleteContents();
          selection.collapseToStart();
          
          // Try to focus after a delay - sometimes this works when immediate focus doesn't
          const container = containerRef.current;
          setTimeout(() => {
            container.focus({ preventScroll: true });
            // Place cursor at end of content or where deletion happened
            const sel = window.getSelection();
            if (sel && container.childNodes.length > 0) {
              sel.collapseToEnd();
            }
          }, 0);
        }
        
        // Notify of content change via callback
        if (onContentChange) {
          onContentChange(containerRef.current.innerHTML);
        }
      }
    };

    // Use capture phase to run before the target element's handler
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [containerRef, onContentChange]);

  return {
    bubbleMenu,
    selectedText,
    setBubbleMenu,
    setSelectedText,
    hideBubbleMenu
  };
};
